import { useLayoutEffect, useRef, useState } from 'react';

/** Measure real text; no arbitrary max-height or duplicated accessible content. */
export function CollapsibleText({
  expanded,
  lines,
  children,
  id,
  className = '',
  onOverflowChange,
}: {
  expanded: boolean;
  lines: number;
  children: string;
  id?: string;
  className?: string;
  onOverflowChange?(overflow: boolean): void;
}) {
  const inner = useRef<HTMLSpanElement>(null);
  const [size, setSize] = useState<{ full: number; short: number } | null>(null);
  useLayoutEffect(() => {
    const node = inner.current;
    if (!node) return;
    const measure = () => {
      const style = getComputedStyle(node);
      const line = parseFloat(style.lineHeight) || (parseFloat(style.fontSize) || 16) * 1.5;
      const full = node.scrollHeight;
      const short = Math.min(full, line * lines);
      setSize((old) => (old?.full === full && old.short === short ? old : { full, short }));
      onOverflowChange?.(full > short + 1);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(node);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [children, lines, onOverflowChange]);
  return (
    <span
      id={id}
      className={`dui-collapsible-text ${className}`}
      data-expanded={expanded}
      style={size ? { height: expanded ? size.full : size.short } : undefined}
    >
      <span ref={inner} className="dui-collapsible-text__inner">
        {children}
      </span>
    </span>
  );
}
